// Client-side prompt-search scoring for the facilitators directory,
// mirroring event-prompt-score.ts. Pure functions — no React, no IO.
// Substring-match per token, weighted sum over the facilitator's offerings.

import { tokenize } from "./event-prompt-score"

type FacilitatorLike = {
  user_id: string
  full_name?: string | null
  professional_title?: string | null
  short_bio?: string | null
  primary_focus?: string | null
  primary_focus_areas?: string[]
  profile_tags?: string[]
  qualifications_text?: string | null
}

// Field weights:
//   profile_tags                                        -> 3
//   primary_focus / primary_focus_areas / title / name  -> 2
//   short_bio / qualifications_text                     -> 1
function haystack(f: FacilitatorLike): { text: string; weight: number }[] {
  const out: { text: string; weight: number }[] = []
  const push = (w: number, ...parts: (string | null | undefined)[]) => {
    for (const p of parts) if (p) out.push({ text: p.toLowerCase(), weight: w })
  }
  for (const t of f.profile_tags ?? []) push(3, t)
  push(2, f.primary_focus, f.professional_title, f.full_name)
  for (const a of f.primary_focus_areas ?? []) push(2, a)
  push(1, f.short_bio, f.qualifications_text)
  return out
}

export function scoreFacilitator(f: FacilitatorLike, tokens: string[]): number {
  if (tokens.length === 0) return 0
  const hs = haystack(f)
  let score = 0
  for (const tok of tokens) {
    for (const { text, weight } of hs) {
      if (text.includes(tok)) score += weight
    }
  }
  return score
}

export function scoreFacilitatorsByPrompt<F extends FacilitatorLike>(
  facilitators: F[],
  prompt: string,
): { facilitator: F; score: number }[] {
  const tokens = tokenize(prompt)
  if (tokens.length === 0) return facilitators.map((facilitator) => ({ facilitator, score: 0 }))
  return facilitators
    .map((facilitator) => ({ facilitator, score: scoreFacilitator(facilitator, tokens) }))
    .filter((x) => x.score > 0)
}
